import React, { useState } from 'react';
import { Brain, Crown, CheckCircle, XCircle, RotateCcw, Trophy, Lightbulb } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';

const QuizDisplay = ({ quiz, documentId }) => {
  const { isPremium } = useAuth();
  const navigate = useNavigate();
  const questions = Array.isArray(quiz) 
    ? quiz 
    : (quiz?.questions || []);

  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  // 1. Premium gate - Show this if they aren't premium
  if (!isPremium) {
    return (
      <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-5">
          <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-brown-400" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Practice Quiz</h3>
          <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs sm:text-sm font-bold rounded-full flex items-center space-x-1">
            <Crown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span>PREMIUM</span>
          </span>
        </div>
        
        <div className="text-center py-6 sm:py-8 md:py-10">
          <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border-2 border-yellow-400/30">
            <Crown className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-yellow-600" />
          </div>
          <h4 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900 mb-2 sm:mb-3">
            Premium Feature
          </h4>
          <p className="text-xs sm:text-sm md:text-base text-brown-600 mb-4 sm:mb-6 px-4">
            Upgrade to Premium to access AI-generated practice quizzes and test your knowledge!
          </p>
         <button 
            onClick={() => navigate(ROUTES.PRICING)}
            className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-semibold rounded-lg sm:rounded-xl transition-all shadow-md text-sm sm:text-base cursor-pointer active:scale-95">
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  // 2. Empty State
if (questions.length === 0) {
      return (
      <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
          <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-brown-400" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Practice Quiz</h3>
        </div>
        <p className="text-xs sm:text-sm md:text-base text-brown-500">No quiz generated for this document yet.</p>
      </div>
    );
  }

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    if (!submitted) {
      setSelectedAnswers({
        ...selectedAnswers,
        [questionIndex]: answerIndex
      });
    }
  };

  const handleSubmit = () => {
    let correctCount = 0;
    quiz.questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correctCount++;
      }
    });
    setScore(correctCount);
    setSubmitted(true);
  };

  const handleRetake = () => {
    setSelectedAnswers({});
    setSubmitted(false);
    setScore(0);
  };

  const percentage = Math.round((score / quiz.questions.length) * 100);

  return (
    <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
      
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 sm:mb-6 gap-2 sm:gap-0">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-brown-600" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Practice Quiz</h3>
        </div>
        {submitted && (
          <div className="flex items-center space-x-2 self-start sm:self-auto">
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            <span className={`text-base sm:text-lg font-bold ${
              percentage >= 60 ? 'text-green-700' : 'text-yellow-700'
            }`}>
              {score}/{quiz.questions.length} ({percentage}%)
            </span>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {quiz.questions.map((question, qIndex) => {
          return (
            <div key={qIndex} className="bg-white/30 rounded-2xl p-4 sm:p-5 border border-cream-200/50">
              {/* Question Label */}
              <div className="flex items-start space-x-3 mb-4">
                <span className="flex-shrink-0 w-7 h-7 bg-brown-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-sm">
                  {qIndex + 1}
                </span>
                <p className="text-base sm:text-lg font-semibold text-brown-900 leading-snug">
                  {question.question}
                </p>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 gap-2.5 ml-0 sm:ml-10">
                {question.options.map((option, oIndex) => {
                  const isSelected = selectedAnswers[qIndex] === oIndex;
                  const isCorrectAnswer = question.correctAnswer === oIndex;
                  
                  // Logic for styling
                  let buttonClass = "bg-white/50 border-cream-300 text-brown-700 hover:bg-cream-50";
                  let icon = <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? 'bg-brown-600' : 'border border-brown-300'}`} />;

                  if (submitted) {
                    if (isCorrectAnswer) {
                      buttonClass = "bg-green-100 border-green-500 text-green-900 font-medium";
                      icon = <CheckCircle className="w-5 h-5 text-green-600" />;
                    } else if (isSelected && !isCorrectAnswer) {
                      buttonClass = "bg-red-100 border-red-400 text-red-900";
                      icon = <XCircle className="w-5 h-5 text-red-500" />;
                    } else {
                      buttonClass = "bg-gray-50 border-gray-200 text-gray-400 opacity-60";
                      icon = <div className="w-2.5 h-2.5 rounded-full border border-gray-300" />;
                    }
                  } else if (isSelected) {
                    buttonClass = "bg-brown-100 border-brown-500 text-brown-900 font-medium";
                  }

                  return (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      disabled={submitted}
                      className={`flex items-center space-x-3 w-full text-left p-4 rounded-xl border-2 transition-all ${buttonClass} ${!submitted && 'hover:-translate-y-0.5 active:translate-y-0'}`}
                    >
                      <div className="flex-shrink-0">{icon}</div>
                      <span className="text-sm sm:text-base">{option}</span>
                    </button>
                  );
                })}
              </div>

              {/* Explanation / Nigerian Context */}
              {submitted && question.explanation && (
                <div className="mt-4 ml-0 sm:ml-10 flex items-start space-x-3 p-4 bg-blue-50/80 border border-blue-100 rounded-xl">
                  <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900 leading-relaxed">
                    <span className="font-bold uppercase text-[10px] tracking-wider block mb-1">Study Note:</span>
                    {question.explanation}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="mt-8 pt-6 border-t border-cream-200">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            disabled={Object.keys(selectedAnswers).length !== quiz.questions.length}
            className="w-full sm:w-auto px-10 py-3.5 bg-brown-700 hover:bg-brown-800 text-white font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:grayscale"
          >
            Submit and See Results
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <button
              onClick={handleRetake}
              className="w-full sm:w-auto px-8 py-3.5 bg-white border-2 border-brown-600 text-brown-700 hover:bg-brown-50 font-bold rounded-xl transition-all flex items-center justify-center space-x-2"
            >
              <RotateCcw className="w-5 h-5" />
              <span>Retake Quiz</span>
            </button>
            <div className={`flex-1 w-full p-3.5 rounded-xl text-center font-bold ${percentage >= 60 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
              {percentage >= 60 ? '🇳🇬 Standard! You sabi this one.' : '📚 Oya, go back and read small.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizDisplay;